// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { aiApi } from '@shared/data-access/api-base/citec-ai-api';
import {
  type SettingsByTypeParams,
  type SearchUniverses,
  type UniverseBasesResp,
  type StockFilters,
  type StocksSummaryResp,
} from '@shared/data-access/interfaces/api/ai/screener';
import { cleanPayload } from '@shared/data-access/utils/clean-payload';
import { screenerStockSummaryPayload } from '@shared/data-access/utils/payloads/ai';
/**
 * Screener API - Citec AI Integration.
 *
 * - This API layer provides access to the **Citec AI Screener API** for retrieving
 *   stock portfolio benchmarks, universe bases, and search functionality.
 *
 * @remarks
 * Use this API to:
 *
 * - Retrieve list of stock portfolio benchmarks.
 *
 * Backed by RTK Query, this module abstracts RESTful operations into React hooks
 * for seamless integration with front-end applications.
 * @see {@link https://citecsolutions.com/apis} for full API reference.
 */
export const screenerApi = aiApi.injectEndpoints({
  endpoints: build => ({
    /**
     * Retrieve list of stock portfolio benchmarks.
     *
     * @remarks
     * This endpoint retrieves a list of detailed benchmarks associated with a
     * specified stock.
     * @example
     *
     * ```tsx
     * const { data } = screenerApi.useGetStockBenchmarksQuery();
     * ```
     *
     * @returns A list of {@link SearchUniverses} entries containing benchmark
     *   metadata.
     */
    getStockBenchmarks: build.query<SearchUniverses[], void>({
      query: () => ({
        url: '/screener/stock/settings/benchmarks/',
        method: 'GET',
      }),
      providesTags: [
        { type: 'Screener', id: 'Benchmarks' },
        { type: 'Screener', id: 'StockSettings' },
        { type: 'Screener', id: 'Settings' },
      ],
    }),

    /**
     * Retrieve settings/filters by asset type and setting type.
     *
     * @remarks
     * This endpoint retrieves available filter options for a specific asset type
     * and setting type. For example, sectors or factors for stocks, categories for
     * mutual funds, etc. The endpoint URL is dynamically constructed based on the
     * asset type.
     * @example
     *
     * ```tsx
     * const { data: sectors } = screenerApi.useGetSettingsByTypeQuery({
     *   assetType: 'stock',
     *   settingType: 'sectors',
     * });
     * ```
     *
     * @param assetType - Type of asset: 'stock', 'etf', or 'mutual_fund'.
     * @param settingType - Type of setting to retrieve: 'sectors', 'factors',
     *   'category', etc.
     * @returns Array of strings representing available filter options.
     */
    getSettingsByType: build.query<string[], SettingsByTypeParams>({
      query: ({ assetType, settingType }) => ({
        url: ['stock', 'etf'].includes(assetType)
          ? `/screener/${assetType}/settings/${settingType}/`
          : `/screener/${assetType}/us/settings/${settingType}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { assetType, settingType }) => [
        { type: 'Screener', id: `Settings-${assetType}-${settingType}` },
        { type: 'Screener', id: 'Settings' },
      ],
    }),
    /**
     * Retrieve list of universe bases for stock screening.
     *
     * @remarks
     * This endpoint retrieves all available universe bases that can be used for
     * stock screening. Universe bases define the initial set of stocks to filter
     * from.
     * @example
     *
     * ```tsx
     * const { data, isLoading, error } =
     *   screenerApi.useGetStockUniverseBaseQuery();
     * ```
     *
     * @returns A list of {@link UniverseBasesResp} entries containing universe base
     *   metadata.
     */
    getStockUniverseBase: build.query<UniverseBasesResp[], void>({
      query: () => ({
        url: '/screener/stock/settings/universe_base/',
        method: 'GET',
      }),
      providesTags: [
        { type: 'Screener', id: 'UniverseBase' },
        { type: 'Screener', id: 'StockSettings' },
        { type: 'Screener', id: 'Settings' },
      ],
    }),

    /**
     * Search universe bases by text query.
     *
     * @remarks
     * This endpoint allows searching for universe bases by name, ticker, or
     * description using a text query. Useful for autocomplete and search
     * functionality.
     * @example
     *
     * ```tsx
     * const { data, isLoading } = screenerApi.useGetStockUniverseSearchQuery({
     *   search_text: 'S&P 500',
     * });
     * ```
     *
     * @param search_text - Text query to search for in universe base names,
     *   tickers, or descriptions.
     * @returns A list of {@link SearchUniverses} entries matching the search query.
     */
    getStockUniverseSearch: build.query<SearchUniverses[], { search_text: string }>(
      {
        query: ({ search_text }) => ({
          url: '/screener/stock/settings/universe_base/search/',
          method: 'GET',
          params: { search_text },
        }),
        providesTags: [
          { type: 'Screener', id: 'UniverseSearch' },
          { type: 'Screener', id: 'StockSettings' },
        ],
      },
    ),

    /**
     * Retrieve stock summary statistics for a given filter configuration.
     *
     * @remarks
     * This endpoint calculates and returns summary statistics (number of stocks,
     * market cap, P/E ratio, P/B ratio, dividend yield) for stocks matching the
     * provided filter criteria. The payload is automatically cleaned before
     * submission.
     * @example
     *
     * ```tsx
     * const [triggerGetStockSummary, { data, isLoading }] =
     *   screenerApi.useLazyGetStockSummaryQuery();
     *
     * const handleGetSummary = async () => {
     *   try {
     *     const summary = await getStockSummary(stockFilters).unwrap();
     *     // Use summary data
     *   } catch (error) {
     *     // Handle error
     *   }
     * };
     * ```
     *
     * @param payload - Stock filter configuration including universe base, factors,
     *   sectors, ESG settings, and inclusion/exclusion lists. Will be cleaned using
     *   `screenerStockSummaryPayload` before submission.
     * @returns Summary statistics object containing aggregated metrics for matching
     *   stocks.
     */

    getStockSummary: build.query<StocksSummaryResp, StockFilters>({
      query: payload => ({
        url: '/screener/stock/summary/',
        method: 'POST',
        body: cleanPayload(payload, screenerStockSummaryPayload),
      }),
      providesTags: [{ type: 'Screener', id: 'StockSummary' }],
    }),
  }),
});
